import React from 'react';
import { Table, Button } from 'react-bootstrap';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { formatPrice } from '../../../../shared/utils/formatPrice';
import { ReportSalesMonthlyResponseV4 } from '../../../../services/cm-group-report/response/ReportSalesMonthlyResponseV4';
import { format } from 'date-fns';

interface ReportSalesMonthlyTableV4Props {
    data: ReportSalesMonthlyResponseV4;
}

const ReportSalesMonthlyTableV4: React.FC<ReportSalesMonthlyTableV4Props> = ({ data }) => {
    const monthHeaders = data.total_month.map(m => m.month);
    const formattedMonthHeaders = monthHeaders.map(m => format(new Date(m), 'MMMM yyyy'));
    const sourceRows = data.total_source;

    const dataMap = new Map<string, Map<string, string>>();
    data.data.forEach(item => {
        if (!dataMap.has(item.name)) {
            dataMap.set(item.name, new Map());
        }
        dataMap.get(item.name)?.set(item.month, item.total_earning);
    });

    const exportToPDF = () => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.text("Sales Monthly Report", 14, 10);
        const head = [["Source", ...formattedMonthHeaders, "Total"]];
        const body = sourceRows.map(source => [
            source.name,
            ...monthHeaders.map(month => {
                const value = Number(dataMap.get(source.name)?.get(month) || 0);
                return value > 0 ? formatPrice(value) : '-';
            }),
            formatPrice(Number(source.total_earning))
        ]);
        const foot = [[
            "Total",
            ...data.total_month.map(monthTotal => formatPrice(Number(monthTotal.total_earning))),
            formatPrice(Number(data.total))
        ]];
        autoTable(doc, { head, body, foot });
        doc.save("ReportSalesMonthlyV4.pdf");
    };

    // ================== FUNGSI YANG DIPERBAIKI DI SINI ==================
    const exportToExcel = () => {
        const header = ["Source", ...formattedMonthHeaders, "Total"];
        const body = sourceRows.map(source => [
            source.name,
            ...monthHeaders.map(month => Number(dataMap.get(source.name)?.get(month) || 0)),
            Number(source.total_earning)
        ]);
        const footer = [
            "Total",
            ...data.total_month.map(monthTotal => Number(monthTotal.total_earning)),
            Number(data.total)
        ];
        const wsData = [header, ...body, footer];
        const ws = XLSX.utils.aoa_to_sheet(wsData);

        // Tetapkan lebar lajur pertama
        ws['!cols'] = [{ wch: 25 }];

        // Gelung melalui semua baris (bermula dari baris kedua, indeks 1)
        // dan lajur kewangan (bermula dari lajur kedua, indeks 1) untuk menetapkan format nombor
        for (let R = 1; R < wsData.length; ++R) {
            for (let C = 1; C < wsData[R].length; ++C) {
                const cell_address = XLSX.utils.encode_cell({ c: C, r: R });
                if (ws[cell_address]) {
                    // Tetapkan jenis sel sebagai nombor ('n') dan format ('z')
                    ws[cell_address].t = 'n';
                    ws[cell_address].z = '#,##0.00';
                }
            }
        }

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Monthly Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "ReportSalesMonthlyV4.xlsx");
    };
    // ====================================================================

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Source</th>
                            {monthHeaders.map((month) => (
                                <th key={month} className="text-end">{format(new Date(month), 'MMMM yyyy')}</th>
                            ))}
                            <th className="text-end">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sourceRows.map(source => (
                            <tr key={source.name}>
                                <td>{source.name}</td>
                                {monthHeaders.map(month => {
                                    const value = Number(dataMap.get(source.name)?.get(month) || 0);
                                    return <td key={month} className="text-end">{value > 0 ? formatPrice(value) : '-'}</td>;
                                })}
                                <td className="text-end"><strong>{Number(source.total_earning) > 0 ? formatPrice(Number(source.total_earning)) : '-'}</strong></td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td><strong>Total</strong></td>
                            {data.total_month.map(monthTotal => (
                                <td key={monthTotal.month} className="text-end"><strong>{Number(monthTotal.total_earning) > 0 ? formatPrice(Number(monthTotal.total_earning)) : '-'}</strong></td>
                            ))}
                            <td className="text-end"><strong>{Number(data.total) > 0 ? formatPrice(Number(data.total)) : '-'}</strong></td>
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </div>
    );
};

export default ReportSalesMonthlyTableV4;